import { useTranslation } from 'next-i18next'
import { CSSProperties, useEffect } from 'react'

import { Breakpoint, useCheckBreakpoint } from '~/utils/useBreakpoint'
import { useAppSelector } from '~/redux/hooks'
import { CategoryColorClasses } from '~/bootcamp/constants/bootcamp.const'
import { selectVideoTopicColor } from '~/bootcamp/slices/vcc-data.slice'
import { isAllowedValue, isDecimalSumComplete } from '~/bootcamp/Activities/utils/activities.utils'
import MultiInput from '~/bootcamp/Activities/Multiplication/components/MultiInput'
import DecimalCommaSlot from '~/bootcamp/Activities/components/DecimalCommaSlot'
import useDecimalCommaPlacement from '~/bootcamp/Activities/hooks/useDecimalCommaPlacement'
import useInAppKeyboardLayout from '~/in-app-keyboard/hooks/useInAppKeyboard'
import keyboardStyles from '~/in-app-keyboard/styles/inAppKeyboard.module.css'

const SOCKET_STAGGER_MS = 70

export default function InputSingleDigitColumnsWithComma({
  className,
  isDecimalError,
  isError,
  onChange,
  clickedDecimalIndex,
  onDecimalClick,
  style,
  values,
  id,
  onEnter,
}: {
  className?: string
  clickedDecimalIndex?: number
  isDecimalError?: boolean
  isError?: boolean
  onChange: (value: string, name: string) => void
  style?: CSSProperties
  values: Record<string, string>
  onDecimalClick: (index?: number) => void
  id?: string
  onEnter?: () => void
}): React.ReactElement {
  const isMobile = useCheckBreakpoint({ lt: Breakpoint.SM })
  const keyboardLayout = useInAppKeyboardLayout(null)
  const fontSize = isMobile ? 18 : 24

  const { t } = useTranslation('bootcamp')
  const decimalSignByLocale = t('signs.decimal')
  const colorInfo: CategoryColorClasses = useAppSelector(selectVideoTopicColor)

  const containerStyle = {
    backgroundBlendMode: 'soft-light, normal',
    fontSize: `${fontSize}px`,
    height: `${fontSize * 2}px`,
  }

  const handleOnChange = (inputVal: string, inputName: string): void => {
    if (isAllowedValue(inputVal) && inputVal.length <= 1) {
      onChange(inputVal, inputName)
    }
  }

  const entries = Object.entries(values)

  const digits = entries.map(([, value]) => value)
  const isSumComplete = isDecimalSumComplete(values)
  const isDecimalPlaced = clickedDecimalIndex !== undefined

  // Right digits with the sign one column off is a place-value slip, so the digits keep their
  // normal styling and only the decimal step is marked — reddening the whole row would tell the
  // pupil their multiplication was wrong when it was not.
  const isSumError = isError && !isDecimalError

  // The choice shows its own meaning: once the sign lands, the digits to its right take a wash,
  // so the pupil can see which digits they have just called decimals. The same wash turns red
  // when the placement is wrong, which points at the mistake instead of merely reporting one.
  const decimalStepClass = isSumError ? 'text-white' : isDecimalError ? 'text-red' : colorInfo.textClass
  const fractionWash = isDecimalError ? 'bg-red bg-opacity-10' : `${colorInfo.bgClass} bg-opacity-10`

  const placement = useDecimalCommaPlacement({
    onPlacedIndexChange: onDecimalClick,
    placedIndex: clickedDecimalIndex,
    slotCount: entries.length - 1,
  })

  // Reads the answer back with the sign in a given column, so a screen reader announces the
  // number a slot would produce rather than an anonymous position.
  const readingFor = (position: number): string =>
    `${digits.slice(0, position).join('') || '0'}${decimalSignByLocale}${digits.slice(position).join('')}`

  // Reopening a digit hides the slots, so an already-chosen sign would keep the answer
  // deliverable while no longer being visible anywhere.
  useEffect(() => {
    if (!isSumComplete && isDecimalPlaced) onDecimalClick(undefined)
  }, [isDecimalPlaced, isSumComplete, onDecimalClick])

  return (
    <div className="flex flex-col items-end">
      <div
        className={`my-1 w-max rounded-small ${decimalStepClass} ${
          isSumError ? ' bg-red' : `outline-0 border-2 ${colorInfo.borderClass}`
        } ${className}`}
        style={{ ...style, ...containerStyle }}
      >
        <div className="relative shadow-input flex" style={{ margin: '-2px', height: 'calc(100% + 4px)' }}>
          {entries.map(([key, value], index) => {
            return (
              <div
                className={`w-8 sm:w-10 relative ${
                  isDecimalPlaced && index >= (clickedDecimalIndex as number) ? fractionWash : ''
                }`}
                id={key}
                key={key}
              >
                <MultiInput
                  className={
                    'h-full w-full bg-transparent focus:rounded-small focus:outline-0 focus:border-2 focus:border-black-light no-input-arrows ' +
                    'text-center text-purple font-semibold placeholder-purple-light ' +
                    `${isSumError ? 'text-white' : 'text-purple hover:bg-purple-light-deep'} ` +
                    `${
                      keyboardLayout
                        ? `${keyboardStyles['input-multiplication-math-field']} ${keyboardStyles['input-common-math-field']}`
                        : ''
                    }`
                  }
                  id={key}
                  isError={isSumError}
                  key={id ? `${id}-${key}` : key}
                  name={key}
                  onChange={handleOnChange}
                  onEnter={onEnter}
                  value={value}
                />
                {entries.length - 1 !== index && (
                  <>
                    <div
                      className={`absolute top-1 md:top-2 right-0 h-[60%] md:h-[70%] w-px ${colorInfo.bgClass} bg-opacity-50`}
                    />
                    {isSumComplete && (
                      <DecimalCommaSlot
                        ariaLabel={t('decimalPlacement.slotAriaLabel', { answer: readingFor(index + 1) })}
                        colorInfo={colorInfo}
                        decimalSign={decimalSignByLocale}
                        enterDelayMs={(entries.length - 2 - index) * SOCKET_STAGGER_MS}
                        index={index + 1}
                        isPlaced={clickedDecimalIndex === index + 1}
                        isRowError={isSumError}
                        isWrong={isDecimalError}
                        onClick={placement.onSlotClick}
                        onKeyDown={placement.onSlotKeyDown}
                        registerSlot={placement.registerSlot}
                      />
                    )}
                  </>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
